// Where a Dashmote staff member's onboarding answers live.
//
// Staff default to Power User in every workspace and hold no single persona,
// so running the wizard is a spot-check of someone else's view rather than a
// setup of their own. Those answers get a session: they survive a refresh,
// die with the tab, and are cleared at logout, so the next sign-in starts
// from the Power User default again. Nothing reaches the server, which is
// what keeps a spot-check off the staff member's stored preferences and out
// of a second device.
//
// Every read is stamped with the uid it was written for. Two people sign in
// on one machine, and an unstamped blob would hand the second one the first
// one's view.

import type { PersonaConfig } from "../data/personaConfigTypes";

// Module-private: the three accessors below are the whole interface, so
// nothing outside needs the key. The E2E spec names the string directly,
// because it reads the value from inside the browser.
const STAFF_PERSONA_SESSION_KEY = "dashone:staff-persona-config";

export type StaffPersonaWorkspaces = Record<string, PersonaConfig>;

export interface StaffPersonaSession {
  uid: string | null;
  byWorkspace: StaffPersonaWorkspaces;
}

export const EMPTY_STAFF_PERSONA_SESSION: StaffPersonaSession = {
  uid: null,
  byWorkspace: {},
};

// Every accessor swallows its own failure. sessionStorage throws in a
// locked-down profile, and the caller's fallback — the Power User default —
// is the same answer a miss produces, so there is nothing for a handler to
// decide.

// The workspaces stored for this uid, empty when the blob is missing,
// unparseable, or belongs to somebody else.
export function readStaffPersonaWorkspaces(
  uid: string,
): StaffPersonaWorkspaces {
  try {
    const raw = sessionStorage.getItem(STAFF_PERSONA_SESSION_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const session = parsed as Partial<StaffPersonaSession>;
    if (session.uid !== uid) return {};
    const byWorkspace = session.byWorkspace;
    return byWorkspace && typeof byWorkspace === "object" ? byWorkspace : {};
  } catch {
    return {};
  }
}

export function writeStaffPersonaSession(session: StaffPersonaSession): void {
  try {
    sessionStorage.setItem(
      STAFF_PERSONA_SESSION_KEY,
      JSON.stringify(session),
    );
  } catch {
    // A refused write costs the refresh-survives-it property, nothing more:
    // the config still resolves from the slice for the rest of the session.
  }
}

export function clearStaffPersonaSession(): void {
  try {
    sessionStorage.removeItem(STAFF_PERSONA_SESSION_KEY);
  } catch {
    // Nothing to fall back to, and nothing that reads it afterwards: the
    // clear runs at logout and at a boot that rejects the stored owner.
  }
}
